import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useLookups } from '../../../hooks/useLookups';

const PersonalInfoStep = ({ formData, onChange, errors, includeIdentityDocuments = false }) => {
  const { items: countries, loading: countriesLoading } = useLookups('COUNTRY');

  // Use only the lookup data without fallbacks
  const countryOptions = useMemo(() => {
    return [...countries];
  }, [countries]);
  const handleChange = (field) => (event) => {
    onChange({ ...formData, [field]: event.target.value });
  };

  const handleDateChange = (field) => (newValue) => {
    onChange({ ...formData, [field]: newValue });
  };

  const handleFileChange = (field) => (event) => {
    const file = event.target.files?.[0] || null;
    onChange({ ...formData, [field]: file });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Personal Information
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Enter the director's personal details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={handleChange('firstName')}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange('lastName')}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Emirates ID"
            value={formData.emiratesId}
            onChange={handleChange('emiratesId')}
            placeholder="784-XXXX-XXXXXXX-X"
            error={!!errors.emiratesId}
            helperText={errors.emiratesId || 'Format: 784-XXXX-XXXXXXX-X'}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Passport Number"
            value={formData.passportNumber}
            onChange={handleChange('passportNumber')}
            error={!!errors.passportNumber}
            helperText={errors.passportNumber}
          />
        </Grid>
        {includeIdentityDocuments && (
          <>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Passport Expiry Date"
                value={formData.passportExpiryDate || null}
                onChange={handleDateChange('passportExpiryDate')}
                inputFormat="dd/MM/yyyy"
                renderInput={(params) => (
                  <TextField {...params} fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Emirates ID Expiry Date"
                value={formData.emiratesIdExpiryDate || null}
                onChange={handleDateChange('emiratesIdExpiryDate')}
                inputFormat="dd/MM/yyyy"
                renderInput={(params) => (
                  <TextField {...params} fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Identity Documents (optional)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                Upload new copies to replace files on file. Expiry dates above are saved with the director profile.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />} fullWidth>
                {formData.passportFile ? 'Replace Passport Copy' : 'Upload Passport Copy'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('passportFile')}
                />
              </Button>
              {formData.passportFile && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {formData.passportFile.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />} fullWidth>
                {formData.emiratesIdFile ? 'Replace Emirates ID Copy' : 'Upload Emirates ID Copy'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('emiratesIdFile')}
                />
              </Button>
              {formData.emiratesIdFile && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {formData.emiratesIdFile.name}
                </Typography>
              )}
            </Grid>
          </>
        )}
        <Grid item xs={12} md={6}>
          <FormControl required fullWidth error={!!errors.nationality}>
            <InputLabel>Nationality</InputLabel>
            <Select
              value={formData.nationality}
              onChange={handleChange('nationality')}
              label="Nationality"
              disabled={countriesLoading}
            >
              {countriesLoading && (
                <MenuItem value="" disabled>
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                  Loading countries…
                </MenuItem>
              )}
              {countryOptions.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.label}
                </MenuItem>
              ))}
            </Select>
            {errors.nationality && (
              <FormHelperText>{errors.nationality}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Date of Birth *"
            value={formData.dateOfBirth}
            onChange={handleDateChange('dateOfBirth')}
            inputFormat="dd/MM/yyyy"
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                required
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl required fullWidth error={!!errors.gender}>
            <FormLabel>Gender</FormLabel>
            <RadioGroup
              row
              value={formData.gender}
              onChange={handleChange('gender')}
            >
              <FormControlLabel value="MALE" control={<Radio />} label="Male" />
              <FormControlLabel value="FEMALE" control={<Radio />} label="Female" />
            </RadioGroup>
            {errors.gender && (
              <FormHelperText>{errors.gender}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalInfoStep;
